import { Flex, Typography } from "antd"
import React, { useEffect, useMemo } from "react"
import { useSearchParams } from "react-router-dom"

import { NotAllowed } from "../../components/PermissionGate/NotAllowed.tsx"
import { generateDocumentTitle } from "../../constants/document-title.ts"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import Icon from "../../Icon"
import LeadsInactivePage from "../Leads/LeadsInactivePage"
import { ConnectedProjectsArchivePage } from "../Projects/ProjectsListPage/ProjectsListPage.tsx"
import {
  canSeeLeadsArchiveContent,
  canSeeProjectsArchive
} from "./archive-permissions.ts"
import { ArchiveTabs } from "./ArchiveTabs.tsx"

const { Title, Text } = Typography

const TAB_LEADS = "leads"
const TAB_PROJECTS = "projects"

/**
 * The centralized home for both archives. It owns nothing of its own — each
 * tab mounts the exact page that used to live at `/leads/inactive` and inside
 * the Projects page's Archive view, so every filter, column and action
 * carries over as-is. Only the entry point moved, and the page chrome: both
 * children render `embedded`, which drops the standalone title block and the
 * document title they would otherwise stack under this page's own header.
 */
export default function ArchivePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const canLeads = useAppSelector((state) =>
    canSeeLeadsArchiveContent(state.auth.permissions)
  )
  const canProjects = useAppSelector((state) =>
    canSeeProjectsArchive(state.auth.permissions)
  )

  const requestedTab = searchParams.get("tab")
  const activeTab = useMemo(() => {
    if (requestedTab === TAB_PROJECTS && canProjects) return TAB_PROJECTS
    if (requestedTab === TAB_LEADS && canLeads) return TAB_LEADS
    // Default to whichever of the two this user actually has, Leads first —
    // matching the [ Leads ] [ Projects ] order everywhere else. `null` when
    // neither: falling back to a tab here would render an archive the user
    // was refused, which is exactly the bypass the route gate exists to stop.
    if (canLeads) return TAB_LEADS
    if (canProjects) return TAB_PROJECTS
    return null
  }, [requestedTab, canLeads, canProjects])

  // Read from whatever each screen has already fetched for itself — a tab
  // that hasn't been visited yet in this session simply shows no count yet,
  // rather than this page fetching anything of its own to populate one. Both
  // slices initialise their counts to a real `0` and the projects filters are
  // persisted across reloads, so the "has it actually loaded" flag is what
  // separates a true zero from an unfetched one; without it every first visit
  // rendered a false `0`.
  const leadsCount = useAppSelector((state) =>
    state.leads?.inactiveCountsLoaded ? state.leads?.inactiveCounts?.total : undefined
  )
  const projectsArchiveLoaded = useAppSelector(
    (state) =>
      state.projects?.listDataLoaded &&
      state.projects?.listFilters?.view === "archive"
  )
  const projectsCount = useAppSelector((state) =>
    projectsArchiveLoaded ? state.projects?.listData?.count : undefined
  )

  // Re-asserted per tab: each child sets its own document title on mount, so
  // switching tabs would otherwise leave the browser tab reading "Projects"
  // while the user is looking at the Archive.
  useEffect(() => {
    generateDocumentTitle("Archive")
  }, [activeTab])

  return (
    <div className={"grid"}>
      <Flex gap={"middle"} vertical className={"min-w-0"}>
        <Flex vertical gap={"middle"}>
          <Flex gap={"middle"} align={"center"}>
            <Flex
              align={"center"}
              justify={"center"}
              className={"size-11 shrink-0 rounded-xl bg-primary/10 text-lg text-primary"}
            >
              <Icon type={"Archive"} />
            </Flex>
            <Flex vertical>
              <Title className={"text-start m-0"} level={2}>
                Archive
              </Title>
              <Text type={"secondary"}>
                View and manage archived leads and projects.
              </Text>
            </Flex>
          </Flex>

          {canLeads && canProjects && (
            <ArchiveTabs
              value={activeTab as string}
              onChange={(next) =>
                setSearchParams(
                  (prev) => {
                    prev.set("tab", next)
                    return prev
                  },
                  { replace: true }
                )
              }
              options={[
                {
                  value: TAB_LEADS,
                  label: "Leads",
                  icon: <Icon type={"Leads"} />,
                  count: leadsCount
                },
                {
                  value: TAB_PROJECTS,
                  label: "Projects",
                  icon: <Icon type={"Projects"} />,
                  count: projectsCount
                }
              ]}
            />
          )}
        </Flex>

        {activeTab === null && (
          <NotAllowed description={"You don't have permission to view the archive."} />
        )}
        {activeTab === TAB_LEADS && <LeadsInactivePage embedded />}
        {activeTab === TAB_PROJECTS && <ConnectedProjectsArchivePage embedded />}
      </Flex>
    </div>
  )
}
